<template>
  <v-navigation-drawer class="mobile_menu_panel pa-0" location="bottom" :width="400" temporary touchless v-model="appStates.mobilePanelOpen">
    <!-- Skeleton Loader shown while loading -->
    <v-list v-if="props.loading" dense nav>
      <div>
        <v-skeleton-loader class="menu_skeleton_loader px-0" width="100%" max-width="100%" color="transparent" type="list-item-avatar" v-for="n in 7" :key="n"> </v-skeleton-loader>
      </div>
    </v-list>
    <!-- Ends -->
    <!-- Render Mobile Menus Here -->
    <v-expansion-panels v-else v-model="panelIndex" class="ma-0 pa-0 mobile_menu_expansions" elevation="0" multiple>
      <v-expansion-panel v-for="(panel, i) in menuPanels" :key="i" class="rounded-0 ma-0" color="primary" :readonly="i === 0" :hide-actions="panel.hideActions">
        <v-expansion-panel-title class="ma-0 pa-5 rounded-0" static>
          <v-list-item id="menu_panel_title" class="ma-0 pa-0 lang_translate" :title="panel.title" variant="text" style="color: rgb(var(--v-theme-secondary))" />
          <v-icon v-if="panel.closePanel" icon="mdi-close" class="ms-auto" @click.stop="appStates.closeMobilePanel()" />
        </v-expansion-panel-title>

        <v-expansion-panel-text class="ma-0m pa-0" :style="`border-width: 0 0 0 ${panel.borderLeft || '0px'} !important`">
          <v-list density="comfortable" nav class="ma-0 pa-0">
            <v-list-item
              v-for="(item, j) in panel.items"
              :key="j"
              class="menu_panel_item lang_translate"
              :to="item.to"
              :href="item.href"
              :target="item.target"
              :title="item.title"
              :append-icon="item.appendIcon"
              @click="
                () => {
                  item.click?.();
                  appStates.closeMobilePanel();
                }
              " />
          </v-list>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
    <!-- Ends -->

    <!-- Action Buttons & NBG Section -->
    <div class="px-5 py-4 d-flex flex-column ga-3" style="border-top: 2px solid rgb(var(--v-theme-primary-800))">
      <v-list v-if="isLoggedIn" density="comfortable" nav class="ma-0 pa-0">
        <v-list-item
          v-if="ngbItem && ngbItem.href"
          class="menu_panel_item-ngb pa-0 ga-2 ma-0 lang_translate"
          :prepend-avatar="getResourceUrl('/' + ngbItem.img.replace(/^\/+/, ''))"
          :href="ngbItem.href"
          target="_blank"
          :title="String(ngbItem.text).toUpperCase()"
          append-icon="mdi-open-in-new"
          base-color="white"
          variant="plain" />
      </v-list>
      <Button v-if="isLoggedOut" :slim="true" version="primary--800" label="Login" style="width: 100%" @click="router.push('/menu_login?action=logout')" />
      <Button v-if="isLoggedOut" :slim="true" version="action" label="Create Account" style="width: 100%" @click="router.push('/register')" />
    </div>
    <!-- Ends -->
  </v-navigation-drawer>
</template>

<script lang="ts" setup>
import { useRouter } from 'vue-router';
import { ref, computed, watch } from 'vue';
import { useAppStates } from '@/store/appStates';
import { useUserStore } from '@/store/userJWTStore';
import { useUserDataStore } from '@/store/userDataStore';
import { getResourceUrl, sportTitle } from '@/services/admin_actions/apiUtils';

import Button from '@/components/ComponentLibrary/Button.vue';

// Define functions for Mobile Menu
const store = useUserStore();
const appStates = useAppStates();
const userDataStore = useUserDataStore();
const isLoggedIn = computed(() => store.isLoggedIn);
const isLoggedOut = computed(() => store.isLoggedOut);
const router = useRouter();

// Define the props for the skeleton loader
const props = defineProps({
  loading: { type: Boolean, default: false },
});

// Define Mobile Menu Panel items
type PanelItem = {
  title: string;
  to?: string;
  href?: string;
  target?: string;
  appendIcon?: string;
  click?: () => void;
};

// Define Mobile Menu Panel
type MenuPanel = {
  title: string;
  icon?: string;
  closePanel?: boolean;
  hideActions?: boolean;
  items: PanelItem[];
  borderLeft?: string;
};

// Function to map the menu items to the mobile menu panels
const menuPanels = computed<MenuPanel[]>(() => {
  const panels: MenuPanel[] = [];

  // Popular In App (left_menu_items)
  const leftMenu = userDataStore.menuData.left_menu_items || {};
  const leftItems: PanelItem[] = Object.values(leftMenu).map((item: any) => ({
    title: item.menu_item || item.title,
    href: item.href?.startsWith('http') ? item.href : undefined,
    to: item.href && !item.href.startsWith('http') ? `/${item.href}` : undefined,
    target: item.href?.startsWith('http') ? '_blank' : undefined,
    appendIcon: item.href?.startsWith('http') ? 'mdi-open-in-new' : undefined,
  }));
  panels.push({ title: 'My Menu', items: leftItems, closePanel: true, hideActions: true });

  // Members Area Section
  const membersHref = userDataStore.menuData.site_map['Members area']?.href || {};
  const membersItems: PanelItem[] = Object.entries(membersHref).map(([key, val]: any) => ({
    title: key,
    href: val.href?.startsWith('http') ? val.href : undefined,
    to: val.href && !val.href.startsWith('http') ? `/${val.href}` : undefined,
    target: val.href?.startsWith('http') ? '_blank' : undefined,
    appendIcon: val.href?.startsWith('http') ? 'mdi-open-in-new' : undefined,
  }));
  panels.push({ title: 'Members Area', items: membersItems, borderLeft: '8px' });

  // My Rankings Section
  const rankingsHref = userDataStore.menuData.site_map['My rankings']?.href || {};
  const rankingItems: PanelItem[] = Object.entries(rankingsHref).map(([key, val]: any) => ({
    title: key,
    href: val.href?.startsWith('http') ? val.href : undefined,
    to: val.href && !val.href.startsWith('http') ? `/${val.href}` : undefined,
    target: val.href?.startsWith('http') ? '_blank' : undefined,
    appendIcon: val.href?.startsWith('http') ? 'mdi-open-in-new' : undefined,
  }));
  panels.push({ title: 'My Rankings', items: rankingItems, borderLeft: '8px' });

  // More Info Section
  const aboutHref = userDataStore.menuData.site_map['About']?.href || {};
  const aboutItems: PanelItem[] = Object.entries(aboutHref).map(([key, val]: any) => ({
    title: key,
    href: val.href?.startsWith('http') ? val.href : undefined,
    to: val.href && !val.href.startsWith('http') ? `/${val.href}` : undefined,
    target: val.href?.startsWith('http') ? '_blank' : undefined,
    appendIcon: val.href?.startsWith('http') ? 'mdi-open-in-new' : undefined,
  }));
  panels.push({ title: 'More Info', items: aboutItems, borderLeft: '8px' });

  // Social Media
  panels.push({
    title: 'Social',
    items: [
      {
        title: 'Twitter / X',
        href: 'https://x.com/' + sportTitle.value + 'levels',
        target: '_blank',
        appendIcon: 'mdi-open-in-new',
      },
      {
        title: 'Facebook',
        href: 'https://www.facebook.com/' + sportTitle.value + 'levels',
        target: '_blank',
        appendIcon: 'mdi-open-in-new',
      },
      {
        title: 'Instagram',
        href: 'https://www.instagram.com/' + sportTitle.value + 'levels',
        target: '_blank',
        appendIcon: 'mdi-open-in-new',
      },
      {
        title: 'Help Center',
        href: 'https://support.leveltech.io/hc/en-us',
        target: '_blank',
        appendIcon: 'mdi-open-in-new',
      },
      ...(isLoggedIn.value
        ? [
            {
              title: 'Log Out',
              href: '/menu_login?action=logout&link_location=site_map',
            },
          ]
        : []),
    ],
    hideActions: true,
  });

  return panels;
});

// Function to get the NGB item
const ngbItem = computed(() => {
  const ngb = userDataStore.menuData.site_map?.NGB;
  return ngb && Object.keys(ngb).length > 0
    ? ngb
    : {
        img: '/maya/icons/icon-player-white.svg',
        mobile: true,
        text: 'No NGB Set',
        href: '/',
      };
});

// Function to set the expansion panels to be open by default
const panelIndex = ref<number[]>([0, menuPanels.value.length - 1]);
watch(panelIndex, (val) => {
  if (!val.includes(0)) val.unshift(0);
  const lastIndex = menuPanels.value.length - 1;
  if (!val.includes(lastIndex)) val.push(lastIndex);
});
</script>

<style lang="scss">
// Styling for the Mobile Menu Panel
.mobile_menu_panel {
  // top: 50px !important;
  background-color: rgb(var(--v-theme-primary)) !important;
  border-top: 1px #fff solid !important;
  height: calc(100% - var(--safe-area-inset-top, 0px)) !important;

  // Ensure navigation drawer content is displayed in a column layout
  .v-navigation-drawer__content {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding-top: 50px !important;
    padding-bottom: 60px !important;
  }

  // Set the background/border color of the expansion panel text
  .v-expansion-panel-text {
    background: rgb(var(--v-theme-primary));
    border: solid;
    border-color: rgb(var(--v-theme-primary-800));
  }

  // Set border on the expansion panel title
  .v-expansion-panel-title {
    border: solid;
    border-width: 2px 0px 0px 0px;
    border-color: rgb(var(--v-theme-primary-800));
  }

  // Disable the expansion panel title overlay
  .v-expansion-panel-title__overlay {
    display: none;
  }

  // Removes the default padding and margin from the expansion panel
  .v-expansion-panel-text__wrapper {
    padding: 0px;
  }

  // Remove overlay from the list items to stop double clicks
  .v-list-item__overlay {
    display: none !important;
  }

  // Styling for the NGB section in the mobile menu
  .menu_panel_item-ngb {
    opacity: 1 !important;

    // Remove border radius from the NGB image
    .v-avatar {
      border-radius: 0px;
    }

    // Set size on the NGB image
    .v-avatar.v-avatar--density-comfortable {
      height: 27px;
      width: 27px;
    }

    // Add with to align NBG text center
    .v-list-item__append {
      width: 29px;
    }

    // Set equal width for NGB items
    .v-list-item__spacer {
      width: 8px;
    }

    // Style the NGB title text
    .v-list-item-title {
      text-transform: uppercase !important;
      color: #fff !important;
      font-family: $brandon-grotesque-font;
      font-size: 18px !important;
      font-weight: bold !important;
      text-align: left;
      font-stretch: normal;
      font-style: normal;
      line-height: 1 !important;
      letter-spacing: 2.25px !important;
      text-wrap: balance;
      justify-content: center;
    }
  }
}

// Style the mobile menu individual expansion panels
.mobile_menu_expansions {
  // Set seperate font size for the title of the menu panel
  #menu_panel_title .v-list-item-title {
    font-size: 11px !important;
  }

  // Styles for the menu panel items
  .v-list-item-title {
    text-transform: uppercase !important;
    font-family: $brandon-grotesque-font;
    font-size: 13px !important;
    font-weight: bold !important;
    text-align: left;
    font-stretch: normal;
    font-style: normal;
    line-height: 1 !important;
    letter-spacing: 2.25px !important;
    text-wrap: balance;
  }

  // Styling for the menu panel item containers
  .v-list-item--nav {
    border: solid;
    border-width: 2px 0px 0px 0px;
    border-color: rgb(var(--v-theme-primary-800));
    border-radius: 0%;
    min-height: 50px !important;
    max-height: 50px !important;
    color: #fff;
    margin-bottom: 0px !important;
    padding: 0px 20px !important;
    border-radius: 0px !important;
  }
}
</style>
